import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useIsPro } from "@/hooks/useIsPro";
import { PositionMgmtMini, HedgingMini, SectorRotationMini } from "@/components/DashMiniPanels";
import { RebalanceMini } from "@/components/RebalancePanel";
import { CapacityHeatmap } from "@/components/CapacityHeatmap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  TrendingUp, DollarSign, Activity, Shield, Clock, RefreshCw,
  Zap, AlertTriangle, Settings, Compass, Gauge, Umbrella, ArrowRight,
  ChevronDown, ChevronUp
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";


// ═══ Iter202/203 + Phase C — Stocks vs Options vs Combined P&L ═══
// Series toggle (All/Stocks/Options/Combined) · units toggle ($ / %) · SPY overlay.
function PerformanceBreakdownChart() {
  const [series, setSeries] = useState(null);
  const [view, setView] = useState("realized");
  const [seriesSel, setSeriesSel] = useState("all");
  const [units, setUnits] = useState("$");
  const [range, setRange] = useState("inception");
  const cache = useState(() => ({}))[0];
  useEffect(() => {
    if (cache[view]) { setSeries(cache[view]); return; }
    setSeries(null);
    api.get("/portfolio/performance-series", { params: { view } })
      .then(({ data }) => { cache[view] = data; setSeries(data); })
      .catch(() => setSeries({ has_data: false }));
  }, [view, cache]);
  const colors = { stock: "#3B82F6", options: "#F59E0B", combined: "#10B981", spy: "#A78BFA" };
  const label = (n) => (n === "stock" ? "Stocks only" : n === "options" ? "Options only" : n === "spy" ? "S&P 500" : "Combined");
  const footnotes = {
    realized: "Cumulative realized P&L from closed Trade Log entries, grouped by exit date. Open-position mark-to-market is not included.",
    unrealized: "Open-position mark-to-market: stocks at daily closes (exact); options at intrinsic value from the underlying's closes — time value excluded. Approximation for trend context only.",
    total: "Realized (closed trades, exact) plus open-position mark-to-market (options at intrinsic value — time value excluded).",
  };
  const basis = series?.pct_basis || 100000;
  const isPct = units === "%";
  // Range filter (client-side): keep rows from the cutoff and rebase each
  // cumulative series to 0 at the range start so 1Y/YTD show the period's move.
  let ranged = series?.rows || [];
  if (range !== "inception" && ranged.length) {
    const cutoff = range === "ytd"
      ? `${new Date().getFullYear()}-01-01`
      : new Date(Date.now() - 365 * 864e5).toISOString().slice(0, 10);
    ranged = ranged.filter(r => (r.date || "") >= cutoff);
    if (ranged.length) {
      const b = ranged[0];
      ranged = ranged.map(r => ({
        ...r,
        stock: +(r.stock - b.stock).toFixed(2),
        options: +(r.options - b.options).toFixed(2),
        combined: +(r.combined - b.combined).toFixed(2),
        spy_pct: (r.spy_pct != null && b.spy_pct != null)
          ? +(((1 + r.spy_pct / 100) / (1 + b.spy_pct / 100) - 1) * 100).toFixed(2) : null,
      }));
    }
  }
  const rows = ranged.map(r => isPct
    ? { ...r, stock: +(r.stock / basis * 100).toFixed(2), options: +(r.options / basis * 100).toFixed(2), combined: +(r.combined / basis * 100).toFixed(2) }
    : r);
  const showLine = (k) => seriesSel === "all" || seriesSel === k;
  const hasSpy = rows.some(r => r.spy_pct != null);
  const fmtVal = (v) => isPct ? `${v}%` : `$${Number(v).toLocaleString()}`;
  const Toggle = ({ options, value, onChange, testId }) => (
    <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid={testId}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} className="text-[9px] px-2 py-0.5 transition-colors"
          style={{ background: value === v ? "rgba(59,130,246,0.12)" : "transparent", color: value === v ? "#3B82F6" : "var(--hoip-text-tertiary)" }}
          data-testid={`${testId}-${String(v).replace("%", "pct").replace("$", "usd")}`}>{l}</button>
      ))}
    </div>
  );
  return (
    <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="performance-breakdown-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Performance — Stocks vs Options vs Combined</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Toggle testId="perf-range-toggle" value={range} onChange={setRange}
            options={[["inception", "Since inception"], ["1y", "1Y"], ["ytd", "YTD"]]} />
          <Toggle testId="perf-series-toggle" value={seriesSel} onChange={setSeriesSel}
            options={[["all", "All"], ["stock", "Stocks"], ["options", "Options"], ["combined", "Combined"]]} />
          <Toggle testId="perf-units-toggle" value={units} onChange={setUnits}
            options={[["$", "Nominal $"], ["%", "%"]]} />
          <Toggle testId="perf-view-toggle" value={view} onChange={setView}
            options={[["realized", "Realized"], ["unrealized", "Unrealized"], ["total", "Total"]]} />
          <span className="text-[9px] cursor-help" style={{ color: "var(--hoip-text-tertiary)" }}
            title={`${footnotes[view]}${range !== "inception" ? " Range view rebases each line to $0 at the range start." : ""}${isPct ? ` % of starting capital ($${basis.toLocaleString()}, editable in Settings). SPY = price return from the series start.` : ""}`}>†</span>
        </div>
      </div>
      {!series ? (
        <div className="flex items-center justify-center" style={{ height: 190 }}>
          <RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
        </div>
      ) : !series.has_data || rows.length < 2 ? (
        <div className="flex items-center justify-center text-[10px]" style={{ height: 190, color: "var(--hoip-text-tertiary)" }}>
          Not enough data for this view yet.
        </div>
      ) : (
      <div style={{ width: "100%", height: 190 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "var(--hoip-text-tertiary)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }} tickFormatter={(d) => d?.slice(5)} interval="preserveStartEnd" axisLine={{ stroke: "var(--hoip-border)" }} tickLine={false} />
            <YAxis tick={{ fill: "var(--hoip-text-tertiary)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}
              tickFormatter={(v) => isPct ? `${v}%` : `$${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`} width={52} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: 10 }}
              labelStyle={{ color: "var(--hoip-text-secondary)" }}
              formatter={(v, name) => [name === "spy_pct" ? `${v}%` : fmtVal(v), name === "spy_pct" ? "S&P 500 (price return)" : label(name)]} />
            {showLine("stock") && <Line type="monotone" dataKey="stock" stroke={colors.stock} strokeWidth={1.5} dot={false} isAnimationActive={false} />}
            {showLine("options") && <Line type="monotone" dataKey="options" stroke={colors.options} strokeWidth={1.5} dot={false} isAnimationActive={false} />}
            {showLine("combined") && <Line type="monotone" dataKey="combined" stroke={colors.combined} strokeWidth={2} dot={false} isAnimationActive={false} />}
            {isPct && hasSpy && <Line type="monotone" dataKey="spy_pct" stroke={colors.spy} strokeWidth={1.2} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      )}
      {series?.has_data && rows.length >= 2 && (
        <div className="flex items-center gap-3 flex-wrap mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="perf-legend">
          {showLine("stock") && <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 inline-block" style={{ background: colors.stock }} />Stocks only</span>}
          {showLine("options") && <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 inline-block" style={{ background: colors.options }} />Options only</span>}
          {showLine("combined") && <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 inline-block" style={{ background: colors.combined }} />Combined</span>}
          {isPct && hasSpy && <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 inline-block border-b border-dashed" style={{ borderColor: colors.spy }} />S&P 500 (price return)</span>}
          {!isPct && hasSpy && <span className="italic">Switch to % to overlay S&P 500</span>}
        </div>
      )}
    </div>
  );
}

// ═══ Iter203 — Capacity & Risk panel: margin gauge + capital-at-risk split ═══
function CapacityRiskPanel({ s }) {
  const [alloc, setAlloc] = useState(null);
  useEffect(() => {
    api.get("/portfolio/risk-allocation").then(({ data }) => setAlloc(data))
      .catch((e) => console.warn("risk-allocation fetch failed:", e?.response?.status || e?.message));
  }, []);
  if (!alloc) {
    // keep the panel mounted (stable testid) while its data loads
    return (
      <div className="border p-3 flex items-center gap-2 text-[10px]"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
        data-testid="capacity-risk-panel">
        <RefreshCw className="w-3 h-3 animate-spin" /> Margin Utilization / Capital at Risk — loading…
      </div>
    );
  }
  const nlv = s.nlv || s.net_liq || 0;
  const util = s.margin_used_pct != null ? Math.max(0, s.margin_used_pct)
    : nlv > 0 ? Math.max(0, (s.maint_margin || 0) / nlv * 100) : 0;
  const cap = alloc.max_margin_utilization || 40;
  const utilColor = util >= cap ? "#EF4444" : util >= cap * 0.8 ? "#F59E0B" : "#10B981";
  const totalRisk = (alloc.stock_at_risk || 0) + (alloc.options_at_risk || 0);
  const stockPct = totalRisk > 0 ? alloc.stock_at_risk / totalRisk * 100 : 0;
  const fmt = (v) => `$${Math.round(v).toLocaleString()}`;
  return (
    <div className="border p-3 grid grid-cols-1 md:grid-cols-2 gap-4"
      style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="capacity-risk-panel">
      <div data-testid="margin-utilization-gauge">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Margin Utilization</span>
          <span className="text-[10px] font-mono-data" style={{ color: utilColor }}>
            {util.toFixed(1)}% <span style={{ color: "var(--hoip-text-tertiary)" }}>/ cap {cap}%</span>
          </span>
        </div>
        <div className="relative h-2.5 rounded-sm overflow-hidden" style={{ background: "var(--hoip-hover)" }}
          title={`Maintenance margin (${fmt(s.maint_margin || 0)}) as % of Net Liquidation Value (${fmt(nlv)}), against the Max Margin Utilization cap you set in Settings (${cap}%).`}>
          <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${Math.min(util, 100)}%`, background: utilColor }} />
          <div className="absolute inset-y-0 w-0.5" style={{ left: `${Math.min(cap, 100)}%`, background: "var(--hoip-text-secondary)" }} title={`Your cap: ${cap}%`} />
        </div>
        <p className="text-[9px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
          Maint margin vs NLV, against your Settings cap (marker). Informational only.
        </p>
      </div>
      <div data-testid="risk-allocation-bar">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Capital at Risk — Stocks vs Options</span>
          <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{fmt(totalRisk)}</span>
        </div>
        <div className="flex h-2.5 rounded-sm overflow-hidden" style={{ background: "var(--hoip-hover)" }}
          title={`Stocks: ${fmt(alloc.stock_at_risk)} (market value) · Options: ${fmt(alloc.options_at_risk)} (structural max loss, cash-secured convention; covered calls carry no incremental risk beyond the shares).`}>
          <div style={{ width: `${stockPct}%`, background: "#3B82F6" }} />
          <div style={{ width: `${100 - stockPct}%`, background: "#F59E0B" }} />
        </div>
        <div className="flex items-center gap-3 mt-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          <span><span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: "#3B82F6" }} />Stocks {fmt(alloc.stock_at_risk)}</span>
          <span><span className="inline-block w-2 h-2 rounded-sm mr-1 align-middle" style={{ background: "#F59E0B" }} />Options {fmt(alloc.options_at_risk)}</span>
        </div>
      </div>
    </div>
  );
}


function MetricCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="border p-2" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</span>
        {Icon && <Icon className="w-2.5 h-2.5" style={{ color: color || "var(--hoip-text-tertiary)" }} />}
      </div>
      <div className="font-mono-data text-xs font-medium truncate" style={{ color: color || "var(--hoip-text-primary)" }}>{value}</div>
      {sub && <div className="text-[9px] mt-0.5 truncate" style={{ color: "var(--hoip-text-secondary)" }}>{sub}</div>}
    </div>
  );
}

// ═══ Row 1 — Portfolio Risk Gauge (radial restatement of existing tiers) + condensed VaR/scenarios ═══
function RiskGaugePanel({ s }) {
  const [g, setG] = useState(null);
  const [risk, setRisk] = useState(null);
  useEffect(() => {
    api.get("/dashboard/risk-gauge").then(({ data }) => setG(data)).catch((e) => console.warn("risk-gauge:", e?.message));
    api.get("/portfolio/risk-analysis").then(({ data }) => setRisk(data)).catch((e) => console.warn("risk-analysis:", e?.message));
  }, []);
  const tierColors = { LOW: "#10B981", MID: "#F59E0B", HIGH: "#EF4444" };
  const color = tierColors[g?.tier] || "#71717A";
  const score = g?.score ?? 0;
  const polar = (p, rr) => { const th = (180 - p * 1.8) * Math.PI / 180; return [62 + rr * Math.cos(th), 58 - rr * Math.sin(th)]; };
  const seg = (p0, p1, col, op) => {
    const [x0, y0] = polar(p0, 44); const [x1, y1] = polar(p1, 44);
    return <path d={`M ${x0} ${y0} A 44 44 0 0 1 ${x1} ${y1}`} stroke={col} strokeWidth="9" fill="none" opacity={op} />;
  };
  const [nx, ny] = polar(score, 33);
  const floor = s?.effective_floor;
  return (
    <div className="border p-3 flex flex-col" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="risk-gauge-panel">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>Portfolio Risk</span>
        <span className="text-[8px] px-1.5 py-0.5 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.10)" }}>Portfolio-specific</span>
      </div>
      <div className="flex items-start gap-3 flex-wrap">
        {/* Radial gauge */}
        <div className="shrink-0" data-testid="risk-gauge-dial">
          {!g ? (
            <div className="w-[124px] h-[74px] flex items-center justify-center"><RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>
          ) : (
            <svg width="124" height="76" viewBox="0 0 124 76">
              {seg(0, 40, "#10B981", 0.85)}{seg(40, 70, "#F59E0B", 0.85)}{seg(70, 100, "#EF4444", 0.85)}
              <line x1="62" y1="58" x2={nx} y2={ny} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="62" cy="58" r="3.5" fill={color} />
              <text x="62" y="74" textAnchor="middle" fontSize="11" fontWeight="700" fill={color} fontFamily="'JetBrains Mono', monospace" data-testid="risk-gauge-tier">{g.tier}</text>
              <text x="14" y="74" fontSize="7" fill="var(--hoip-text-tertiary)">Low</text>
              <text x="98" y="74" fontSize="7" fill="var(--hoip-text-tertiary)">High</text>
            </svg>
          )}
          {g && (
            <p className="text-[8px] mt-0.5 leading-snug w-[124px]" style={{ color: "var(--hoip-text-tertiary)" }}
              title={`β-weighted exposure $${(g.leverage?.beta_weighted_gross || 0).toLocaleString()} gross → $${(g.leverage?.beta_weighted_net || 0).toLocaleString()} net of protective-put coverage`}>
              {g.breach_count} rule breach{g.breach_count === 1 ? "" : "es"} · β-lev ×{g.leverage?.net_ratio ?? "—"} net{g.escalated_by_leverage ? " (escalated)" : ""}
            </p>
          )}
        </div>
        {/* Condensed VaR block */}
        <div className="flex-1 min-w-[180px] grid grid-cols-2 gap-x-3 gap-y-1" data-testid="risk-gauge-var-block">
          {[["VaR 95%", risk?.var_95 != null ? `$${risk.var_95.toLocaleString()}` : "…", "#F87171",
             "Value at Risk — in 95% of simulated outcomes over the projection horizon, the portfolio's loss stays below this amount. Roughly a 1-in-20 chance of losing more."],
            ["CVaR 95%", risk?.cvar_95 != null ? `$${risk.cvar_95.toLocaleString()}` : "…", "#F87171",
             "Conditional VaR (expected shortfall) — the AVERAGE loss across the worst 5% of simulated outcomes, i.e. how bad it typically gets once VaR is exceeded."],
            ["Breach P", risk?.breach_probability != null ? `${risk.breach_probability}%` : "…", risk?.breach_probability > 15 ? "#EF4444" : risk?.breach_probability > 5 ? "#F59E0B" : "#10B981",
             "Breach probability — the share of Monte Carlo simulations where portfolio value falls below your protection floor within the projection horizon."],
            ["Portfolio Vol", risk?.portfolio_vol != null ? `${risk.portfolio_vol}%` : "…", "var(--hoip-text-primary)",
             "Annualized volatility of the whole portfolio, estimated from each position's recent price swings and its weight. Higher = wider expected value swings."]].map(([l, v, c, tip]) => (
            <div key={l} className="cursor-help" title={tip}>
              <span className="text-[8px] block border-b border-dotted w-fit" style={{ color: "var(--hoip-text-tertiary)", borderColor: "var(--hoip-border)" }}>{l}</span>
              <span className="font-mono-data text-[11px] font-medium" style={{ color: c }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Scenario rows */}
      <table className="w-full dense-table text-[9px] mt-1.5" data-testid="risk-gauge-scenarios">
        <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
          <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Scenario</th>
          <th className="text-right font-medium cursor-help border-b border-dotted" style={{ color: "var(--hoip-text-tertiary)", borderColor: "var(--hoip-border)" }}
            title="Shock — an instant market-wide price drop applied to all positions. The 1σ/2σ/3σ rows are sized from your portfolio's own 30-day volatility; COVID/GFC use historical crash magnitudes.">Shock</th>
          <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Est. loss</th>
          <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>NLV</th>
          <th className="text-center font-medium cursor-help border-b border-dotted" style={{ color: "var(--hoip-text-tertiary)", borderColor: "var(--hoip-border)" }}
            title="Floor — whether Net Liquidation Value after the shock stays above your configured protection floor. OK = above the floor · BREACH = the shock would push NLV below it.">Floor</th>
        </tr></thead>
        <tbody>
          {(g?.scenarios || []).map((sc) => {
            const breach = floor != null && sc.stressed_nlv < floor;
            return (
              <tr key={sc.label} className="border-b" style={{ borderColor: "var(--hoip-divider)", background: breach ? "rgba(239,68,68,0.05)" : undefined }}>
                <td className="font-mono-data">{sc.label}</td>
                <td className="text-right font-mono-data">{sc.shock_pct}%</td>
                <td className="text-right font-mono-data text-red-400">${sc.est_loss?.toLocaleString()}</td>
                <td className="text-right font-mono-data">${sc.stressed_nlv?.toLocaleString()}</td>
                <td className="text-center">{floor == null ? "—" : breach
                  ? <span className="text-red-500 font-bold cursor-help" title="BREACH — after this shock, Net Liquidation Value would fall BELOW your protection floor.">BREACH</span>
                  : <span className="text-emerald-500 cursor-help" title="OK — after this shock, Net Liquidation Value stays ABOVE your protection floor.">OK</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[8px] italic mt-auto pt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
        Restates your Settings-defined risk rules + β-weighted exposure net of hedges. Scenarios are mechanical shocks — informational, not advice.
      </p>
    </div>
  );
}

// ═══ Row 2 — Wheel Income trade-log analytics ═══
function WheelAnalyticsPanel() {
  const [w, setW] = useState(null);
  const [view, setView] = useState("premium_net");
  const [range, setRange] = useState("inception");
  const cache = useState(() => ({}))[0];
  useEffect(() => {
    if (cache[range]) { setW(cache[range]); return; }
    setW(null);
    api.get("/dashboard/wheel-analytics", { params: { range } })
      .then(({ data }) => { cache[range] = data; setW(data); })
      .catch((e) => { console.warn("wheel-analytics:", e?.message); setW({ has_data: false }); });
  }, [range, cache]);
  const v = w?.views?.[view] || (w?.has_data ? w : null);
  const stats = v ? [
    ["Win Rate", `${v.win_rate}%`, v.win_rate >= 50 ? "#10B981" : "#EF4444"],
    ["Total P&L", `${v.total_pnl >= 0 ? "+" : "-"}$${Math.abs(v.total_pnl).toLocaleString()}`, v.total_pnl >= 0 ? "#10B981" : "#EF4444"],
    ["Avg P&L", `${v.avg_pnl >= 0 ? "+" : "-"}$${Math.abs(v.avg_pnl).toLocaleString()}`, v.avg_pnl >= 0 ? "#10B981" : "#EF4444"],
    ["Profit Factor", v.profit_factor ?? "∞", "var(--hoip-text-primary)"],
    ["Avg Prem/Strike", v.avg_prem_strike_pct != null ? `${v.avg_prem_strike_pct}%` : "—", "var(--hoip-text-primary)"],
    ["Max Drawdown", `$${(v.max_drawdown || 0).toLocaleString()}`, "#F87171"],
  ] : [];
  const ex = w?.exclusions;
  return (
    <div className="border p-3 flex flex-col" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="wheel-analytics-panel">
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
        <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Wheel Income — Trade Log Analytics</h2>
        <div className="flex items-center gap-1.5">
          <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="wheel-range-toggle">
            {[["inception", "Since inception"], ["1y", "1Y"], ["ytd", "YTD"]].map(([val, l]) => (
              <button key={val} onClick={() => setRange(val)} className="text-[8px] px-1.5 py-0.5 font-bold transition-colors"
                style={{ background: range === val ? "rgba(59,130,246,0.12)" : "transparent", color: range === val ? "#3B82F6" : "var(--hoip-text-tertiary)" }}
                data-testid={`wheel-range-toggle-${val}`}>{l}</button>
            ))}
          </div>
          {w?.has_data && (
            <>
              <span className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
                {v?.trades} {view === "premium_net" ? "premium blocks" : "closed trades"}
              </span>
              <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="wheel-view-toggle">
                {[["premium_net", "Premium (net)"], ["all_strategies", "All strategies"]].map(([val, l]) => (
                  <button key={val} onClick={() => setView(val)} className="text-[8px] px-1.5 py-0.5 font-bold transition-colors"
                    style={{ background: view === val ? "rgba(59,130,246,0.12)" : "transparent", color: view === val ? "#3B82F6" : "var(--hoip-text-tertiary)" }}
                    data-testid={`wheel-view-toggle-${val}`}>{l}</button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {!w ? (
        <div className="flex-1 flex items-center justify-center py-8"><RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>
      ) : !w.has_data || !v ? (
        <div className="flex-1 flex items-center justify-center py-8 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          {range === "inception" ? "No closed trades in the Trade Log yet." : `No trades closed ${range === "ytd" ? "this year" : "in the last 12 months"}.`}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-x-3 gap-y-1.5" data-testid="wheel-analytics-stats">
            {stats.map(([l, val, c]) => (
              <div key={l}>
                <span className="text-[8px] block" style={{ color: "var(--hoip-text-tertiary)" }}>{l}</span>
                <span className="font-mono-data text-[12px] font-medium" style={{ color: c }}>{val}</span>
              </div>
            ))}
          </div>
          <div className="mt-2" style={{ height: 84 }} data-testid="wheel-cum-sparkline">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={v.series} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <XAxis dataKey="date" hide />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: 10 }}
                  formatter={(val) => [`$${Number(val).toLocaleString()}`, "Cumulative P&L"]} />
                <Line type="monotone" dataKey="cum" stroke="#10B981" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[8px] italic mt-1" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="wheel-view-footnote">
            {view === "premium_net"
              ? <>Short premium (CSP / CC / short puts &amp; calls / BPS); long legs opened as part of the same structure (same ticker, entry date &amp; expiry) are netted into their block.
                  {ex ? ` Excluded: ${ex.standalone_hedge_legs} standalone hedge leg${ex.standalone_hedge_legs === 1 ? "" : "s"} (${ex.standalone_hedge_pnl >= 0 ? "+" : "-"}$${Math.abs(ex.standalone_hedge_pnl).toLocaleString()}) and ${ex.other_legs} stock/other leg${ex.other_legs === 1 ? "" : "s"} (${ex.other_pnl >= 0 ? "+" : "-"}$${Math.abs(ex.other_pnl).toLocaleString()})${ex.paired_blocks ? ` · ${ex.paired_blocks} spread block${ex.paired_blocks === 1 ? "" : "s"} netted` : ""}. Switch to "All strategies" to tie to Trade Log → Analytics.` : ""}</>
              : <>Every closed trade in the Trade Log, all strategies — this total matches Trade Log → Analytics.</>}
          </p>
        </>
      )}
    </div>
  );
}

// ═══ Row 5 — Risk Intelligence: market-wide matrix (Tail Risk / Cross-Asset / Execution) ═══
function MarketRiskPanel({ macro }) {
  const m = macro || {};
  const toneColors = { red: "#EF4444", amber: "#F59E0B", ok: "var(--hoip-text-primary)" };
  return (
    <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="market-risk-panel">
      <div className="px-3 py-2 border-b flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Risk Intelligence — Market-Wide</span>
          <span className="text-[8px] px-1.5 py-0.5 rounded-sm" style={{ color: "#3B82F6", background: "rgba(59,130,246,0.10)" }}>VIX · dispersion · tail risk · cross-asset</span>
          {m.stale && <span className="text-[8px] px-1.5 py-0.5 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.10)" }} data-testid="macro-stale-badge">stale — last known good</span>}
        </div>
        {m.as_of && <span className="text-[8px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>as of {new Date(m.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
      </div>
      {m.vix == null ? (
        <div className="px-3 py-4 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Macro data loading…</div>
      ) : (
        <>
          {/* Alerts */}
          {(m.alerts || []).length > 0 && (
            <div className="px-3 py-1.5 border-b space-y-1" style={{ borderColor: "var(--hoip-border)" }} data-testid="macro-alerts">
              {m.alerts.map((a, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[9px]" data-testid={`macro-alert-${i}`}>
                  <AlertTriangle className="w-3 h-3 shrink-0 mt-px" style={{ color: a.severity === "red" ? "#EF4444" : "#F59E0B" }} />
                  <span><span className="font-bold" style={{ color: a.severity === "red" ? "#EF4444" : "#F59E0B" }}>{a.label}</span>
                    <span style={{ color: "var(--hoip-text-tertiary)" }}> — {a.detail}</span></span>
                </div>
              ))}
            </div>
          )}
          <div className="px-3 py-1.5 border-b" style={{ borderColor: "var(--hoip-border)" }}>
            <span className="text-[9px] font-medium" style={{ color: "var(--hoip-text-secondary)" }} data-testid="macro-state-line">{m.state}</span>
            {m.interpretation && <p className="text-[9px] italic mt-0.5 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="macro-interpretation">{m.interpretation}</p>}
          </div>
          {/* Matrix table */}
          <div className="overflow-x-auto">
            <table className="w-full dense-table text-[10px]" data-testid="macro-matrix">
              <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Category</th>
                <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Indicator</th>
                <th className="text-right font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Value</th>
                <th className="text-left font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Contextual Insight</th>
              </tr></thead>
              <tbody>
                {(m.matrix || []).map((row, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="text-[9px] whitespace-nowrap" style={{ color: "var(--hoip-text-tertiary)" }}>{row.category}</td>
                    <td className="text-[9px] whitespace-nowrap" style={{ color: "var(--hoip-text-secondary)" }}>{row.indicator}</td>
                    <td className="text-right font-mono-data font-medium whitespace-nowrap" style={{ color: toneColors[row.tone] || "var(--hoip-text-primary)" }}>
                      {row.value ?? "—"}{row.pctile != null && <span className="text-[8px] font-normal" style={{ color: "var(--hoip-text-tertiary)" }}> p{row.pctile}</span>}
                    </td>
                    <td className="text-[9px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{row.insight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="px-3 py-1.5 text-[8px] italic" style={{ color: "var(--hoip-text-tertiary)" }}>
        Descriptive market internals from Cboe/ICE index feeds (percentiles vs last 6 months). Not a forecast or a signal to trade.
      </p>
    </div>
  );
}

// ═══ Row 6 — (Phase F shipped: RebalanceMini lives in components/RebalancePanel.jsx) ═══

// ═══ Compact Signal Cards (Cockpit Mode) ═══
function HedgingSignalCard() {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portfolio/hedging-program");
      const urgent = (data.hedges || []).filter(h => h.urgency === "URGENT" || h.urgency === "BUY_NOW").length;
      const total = (data.hedges || []).length;
      setCount({ urgent, total });
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  const urgentCount = count?.urgent || 0;
  const totalCount = count?.total || 0;
  const color = urgentCount > 0 ? "#EF4444" : totalCount > 0 ? "#F59E0B" : "#10B981";

  return (
    <div className="border rounded-sm p-3 cursor-pointer hover:bg-[var(--hoip-hover)] transition-colors"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      onClick={() => navigate("/hedging")} data-testid="hedging-signal-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Umbrella className="w-3.5 h-3.5 text-red-400" />
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Hedging</span>
        </div>
        <div className="flex items-center gap-1.5">
          {!count && !loading && (
            <button onClick={(e) => { e.stopPropagation(); scan(); }} className="text-[9px] px-1.5 py-0.5 border rounded-sm" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>Scan</button>
          )}
          {loading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />}
          <ArrowRight className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />
        </div>
      </div>
      {count ? (
        <div className="flex items-center gap-2">
          <span className="font-mono-data text-lg font-bold" style={{ color }}>{urgentCount > 0 ? urgentCount : totalCount}</span>
          <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            {urgentCount > 0 ? `urgent of ${totalCount} positions` : totalCount > 0 ? "positions to review" : "all hedged"}
          </span>
        </div>
      ) : (
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Click to analyze hedge needs</span>
      )}
    </div>
  );
}

function RotationSignalCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const scan = async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/sector-rotation"); setData(d); }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  const rot = data?.rotation || {};
  const phaseLabels = { early_expansion: "Early Expansion", late_expansion: "Late Expansion", mid_cycle: "Mid Cycle", early_contraction: "Early Contraction", late_contraction: "Late Contraction" };
  const phaseColor = { early_expansion: "#10B981", late_expansion: "#F59E0B", mid_cycle: "#3B82F6", early_contraction: "#EF4444", late_contraction: "#EF4444" };

  return (
    <div className="border rounded-sm p-3 cursor-pointer hover:bg-[var(--hoip-hover)] transition-colors"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      onClick={() => navigate("/portfolio")} data-testid="rotation-signal-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Compass className="w-3.5 h-3.5 text-blue-500" />
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Sector Rotation</span>
        </div>
        <div className="flex items-center gap-1.5">
          {!data && !loading && (
            <button onClick={(e) => { e.stopPropagation(); scan(); }} className="text-[9px] px-1.5 py-0.5 border rounded-sm" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>Scan</button>
          )}
          {loading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />}
          <ArrowRight className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />
        </div>
      </div>
      {data ? (
        <div className="space-y-1">
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data" style={{ color: phaseColor[rot.phase], background: `${phaseColor[rot.phase]}15` }}>
            {phaseLabels[rot.phase] || "—"}
          </span>
          <div className="flex gap-2 text-[9px]">
            {rot.accumulate?.length > 0 && <span className="text-emerald-500">↑{rot.accumulate.length} strengthening</span>}
            {rot.reduce?.length > 0 && <span className="text-red-400">↓{rot.reduce.length} weakening</span>}
          </div>
        </div>
      ) : (
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Click to scan sector momentum</span>
      )}
    </div>
  );
}

function RiskSignalCard() {
  const navigate = useNavigate();
  const { requirePro } = useIsPro();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const scan = async () => {
    if (!requirePro("/portfolio/sorm")) return;
    setLoading(true);
    try { const { data: d } = await api.get("/portfolio/sorm"); setData(d); }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  const sorm = data?.sorm || data?.form || {};
  const breachPct = ((sorm.pf_sorm ?? sorm.failure_probability ?? 0) * 100);
  const reliabilityPct = 100 - breachPct;
  const gaugeColor = reliabilityPct >= 90 ? "#10B981" : reliabilityPct >= 75 ? "#F59E0B" : "#EF4444";

  return (
    <div className="border rounded-sm p-3 cursor-pointer hover:bg-[var(--hoip-hover)] transition-colors"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      onClick={() => navigate("/reliability")} data-testid="risk-signal-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Gauge className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Risk Intelligence</span>
        </div>
        <div className="flex items-center gap-1.5">
          {!data && !loading && (
            <button onClick={(e) => { e.stopPropagation(); scan(); }} className="text-[9px] px-1.5 py-0.5 border rounded-sm" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>Scan</button>
          )}
          {loading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />}
          <ArrowRight className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} />
        </div>
      </div>
      {data ? (
        <div className="flex items-center gap-3">
          <span className="font-mono-data text-lg font-bold" style={{ color: gaugeColor }}>{reliabilityPct.toFixed(0)}%</span>
          <div className="text-[9px] space-y-0.5">
            <div style={{ color: "var(--hoip-text-tertiary)" }}>Reliable</div>
            <div style={{ color: gaugeColor }}>Breach: {breachPct.toFixed(1)}%</div>
          </div>
        </div>
      ) : (
        <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Click to analyze floor breach risk</span>
      )}
    </div>
  );
}

// ═══ Top Wheel Candidates (item 9 — Wheel Income data w/ CSP · BPS · CC toggle) ═══
function WheelTopCard() {
  const navigate = useNavigate();
  const [intent, setIntent] = useState("CSP");
  const [fit, setFit] = useState("fit");
  const [cacheMap, setCacheMap] = useState({});
  const [loading, setLoading] = useState(false);
  const wheelAbort = useRef(null);

  const load = async (it, f) => {
    const key = `${it}-${f === "fit" ? "fit" : "all"}`;
    setLoading(true);
    if (wheelAbort.current) wheelAbort.current.abort();
    const ac = new AbortController();
    wheelAbort.current = ac;
    try {
      const { data } = await api.get("/income-engine", {
        params: { mode: "new_entries", strategy_intent: it, batch_size: 20, regime_fit_only: f === "fit" },
        timeout: 8000,
        signal: ac.signal,
      });
      setCacheMap(p => ({ ...p, [key]: data.new_entries || data.entries || [] }));
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      console.warn("wheel top card:", e?.message || e);
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load("CSP", "fit"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const switchIntent = (it) => { setIntent(it); if (!cacheMap[`${it}-${fit === "fit" ? "fit" : "all"}`]) load(it, fit); };
  const switchFit = (f) => { setFit(f); if (!cacheMap[`${intent}-${f === "fit" ? "fit" : "all"}`]) load(intent, f); };
  const all = cacheMap[`${intent}-${fit === "fit" ? "fit" : "all"}`] || [];
  const rows = (fit === "against" ? all.filter(e => e.regime_fit === "against")
    : fit === "fit" ? all.filter(e => e.regime_fit === "aligns")
    : all).slice(0, 10);
  const fitColors = { aligns: "#10B981", against: "#EF4444", disagreement: "#F59E0B" };

  return (
    <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="wheel-top-card">
      <div className="flex items-center justify-between p-3 border-b flex-wrap gap-2" style={{ borderColor: "var(--hoip-border)" }}>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-xs font-bold cursor-pointer hover:underline" style={{ fontFamily: "Chivo, sans-serif" }}
            onClick={() => navigate("/wheel-strategies")} title="Data source: Wheel Income engine — click to open"
            data-testid="wheel-top-title">Top Wheel Candidates</h2>
          <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="wheel-intent-toggle">
            {["CSP", "BPS", "CC"].map(it => (
              <button key={it} onClick={() => switchIntent(it)} className="text-[9px] px-2 py-0.5 font-bold transition-colors"
                style={{ background: intent === it ? "rgba(59,130,246,0.12)" : "transparent", color: intent === it ? "#3B82F6" : "var(--hoip-text-tertiary)" }}
                data-testid={`wheel-intent-${it}`}>{it}</button>
            ))}
          </div>
          <div className="flex rounded-sm border overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="wheel-fit-toggle">
            {[["fit", "Regime-Fit"], ["against", "Against"], ["all", "All"]].map(([f, l]) => (
              <button key={f} onClick={() => switchFit(f)} className="text-[9px] px-2 py-0.5 transition-colors"
                style={{ background: fit === f ? "rgba(16,185,129,0.12)" : "transparent", color: fit === f ? "#10B981" : "var(--hoip-text-tertiary)" }}
                data-testid={`wheel-fit-${f}`}>{l}</button>
            ))}
          </div>
          {loading && <RefreshCw className="w-3 h-3 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />}
        </div>
        <button onClick={() => navigate("/wheel-strategies")} className="text-[10px] underline" style={{ color: "var(--hoip-info)" }} data-testid="view-all-wheel">View All</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full dense-table" data-testid="dashboard-wheel-table">
          <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
            <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
            <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strikes</th>
            <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>DTE</th>
            <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Δ</th>
            <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Prem</th>
            <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Yld/wk</th>
            <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Fit</th>
          </tr></thead>
          <tbody>
            {rows.map((e, i) => (
              <tr key={`${intent}-${e.ticker}-${i}`} className="border-b hover:bg-[var(--hoip-hover)] cursor-pointer" style={{ borderColor: "var(--hoip-divider)" }} onClick={() => navigate("/wheel-strategies")}>
                <td className="font-mono-data text-[10px] font-medium">{e.ticker}</td>
                <td className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{e.strikes_label || e.short_strike}</td>
                <td className="text-right font-mono-data text-[10px]">{e.dte}</td>
                <td className="text-right font-mono-data text-[10px]">{e.delta != null ? `${e.delta_estimated ? "~" : ""}${Math.abs(e.delta).toFixed(2)}` : "—"}</td>
                <td className="text-right font-mono-data text-[10px]">${e.premium_per?.toFixed(2) ?? e.premium?.toFixed(0)}</td>
                <td className="text-right font-mono-data text-[10px]">{e.yield_pct_per_week != null ? `${e.yield_pct_per_week.toFixed(2)}%` : "—"}</td>
                <td className="text-[9px]" style={{ color: fitColors[e.regime_fit] || "var(--hoip-text-tertiary)" }}>{e.regime_fit || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                {loading ? "Screening candidates…" : fit === "against" ? "No against-regime candidates in the current universe."
                  : intent === "CC"
                  ? "No covered-call candidates — CC requires an existing position of ≥100 shares per contract."
                  : "No candidates passed your yield floor / regime filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="px-3 py-1.5 text-[8px] italic border-t" style={{ color: "var(--hoip-text-tertiary)", borderColor: "var(--hoip-divider)" }}>
        Wheel Income engine output under your Settings filters{intent === "CC" ? " — CC shows only tickers where you hold ≥100 shares" : ""}. Informational, not a recommendation.
      </p>
    </div>
  );
}

// ═══ Portfolio Settings Inline ═══
function PortfolioSettings({ s, onSave }) {
  const [open, setOpen] = useState(false);
  const hasCashOverride = s.cash_override != null;
  const [cashMode, setCashMode] = useState(hasCashOverride ? "direct" : "starting");
  const [cashOverride, setCashOverride] = useState(s.cash_override ?? "");
  const [startingCash, setStartingCash] = useState(s.starting_cash || 100000);
  const [floorPct, setFloorPct] = useState(85);
  const [hardFloor, setHardFloor] = useState(s.hard_floor || "");
  const [rate, setRate] = useState(s.interest_rate || 6.83);

  const save = async () => {
    const payload = { floor_pct: floorPct, hard_floor: hardFloor || null, interest_rate: rate };
    if (cashMode === "direct") {
      payload.cash_override = parseFloat(cashOverride) || 0;
    } else {
      payload.starting_cash = startingCash;
      payload.cash_override = null; // clear override
    }
    await onSave(payload);
    setOpen(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-[10px] px-2 py-0.5 border rounded-sm" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }} data-testid="portfolio-settings-btn">
      <Settings className="w-3 h-3" /> Settings
    </button>
  );

  return (
    <div className="border rounded-sm p-3 space-y-2" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="portfolio-settings-panel">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
            Cash Mode
          </label>
          <div className="flex gap-1">
            <button onClick={() => setCashMode("direct")}
              className={`px-2 py-0.5 text-[9px] border rounded-sm ${cashMode === "direct" ? "bg-blue-600 text-white border-blue-600" : "border-[var(--hoip-border)] text-[var(--hoip-text-tertiary)]"}`}>
              Broker Cash
            </button>
            <button onClick={() => setCashMode("starting")}
              className={`px-2 py-0.5 text-[9px] border rounded-sm ${cashMode === "starting" ? "bg-blue-600 text-white border-blue-600" : "border-[var(--hoip-border)] text-[var(--hoip-text-tertiary)]"}`}>
              Starting Capital
            </button>
          </div>
        </div>
        <div>
          {cashMode === "direct" ? (
            <>
              <label className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Cash Balance (from broker)</label>
              <Input type="number" value={cashOverride} onChange={e => setCashOverride(e.target.value)} placeholder="e.g. 15000" className="h-6 w-28 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)]" style={{ color: "var(--hoip-text-primary)" }} data-testid="cash-override-input" />
            </>
          ) : (
            <>
              <label className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Starting Capital</label>
              <Input type="number" value={startingCash} onChange={e => setStartingCash(parseFloat(e.target.value) || 0)} className="h-6 w-28 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)]" style={{ color: "var(--hoip-text-primary)" }} data-testid="starting-cash-input" />
            </>
          )}
        </div>
        <div>
          <label className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Floor % of Peak</label>
          <Input type="number" value={floorPct} onChange={e => setFloorPct(parseFloat(e.target.value) || 85)} className="h-6 w-16 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)]" style={{ color: "var(--hoip-text-primary)" }} />
        </div>
        <div>
          <label className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Hard Floor $</label>
          <Input type="number" value={hardFloor} onChange={e => setHardFloor(e.target.value)} placeholder="None" className="h-6 w-24 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)]" style={{ color: "var(--hoip-text-primary)" }} />
        </div>
        <div>
          <label className="text-[9px] block mb-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>Interest Rate %</label>
          <Input type="number" step="0.01" value={rate} onChange={e => setRate(parseFloat(e.target.value) || 0)} className="h-6 w-16 text-xs font-mono-data bg-[var(--hoip-bg)] border-[var(--hoip-border)]" style={{ color: "var(--hoip-text-primary)" }} />
        </div>
        <Button size="sm" onClick={save} className="h-6 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-3">Save</Button>
        <button onClick={() => setOpen(false)} className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Cancel</button>
      </div>
      {cashMode === "direct" && (
        <p className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          Enter your actual cash balance from your broker. NLV = Cash + Market Value of positions.
        </p>
      )}
      {cashMode === "starting" && (
        <p className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          Initial deposit. Cash = Starting Capital - Cost of Positions + Premiums Received.
        </p>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState({ stocks: [], options: [] });
  const [loading, setLoading] = useState(true);
  const [posRegimes, setPosRegimes] = useState({});
  const [reconAlert, setReconAlert] = useState(null);
  const [miniPanels, setMiniPanels] = useState(null);
  const [metricsOpen, setMetricsOpen] = useState(() => localStorage.getItem("dash_metrics_expanded") === "1");
  const toggleMetrics = () => setMetricsOpen(v => { localStorage.setItem("dash_metrics_expanded", v ? "0" : "1"); return !v; });

  const loadData = useCallback(async () => {
    try {
      const [sumRes, posRes] = await Promise.all([
        api.get("/portfolio/summary"), api.get("/portfolio/positions")
      ]);
      setSummary(sumRes.data);
      setPositions(posRes.data);
      // Iter229 — mini-panels (Settings-driven flags + macro strip), fire-and-forget.
      api.get("/dashboard/mini-panels").then(({ data: mp }) => setMiniPanels(mp)).catch(() => setMiniPanels({}));
      // Iter156 — subtle broker-truth divergence nudge. Fetch reconciliation
      // in the background; if the endpoint reports a `newly_divergent`
      // verdict (something flipped to divergent since last time this user
      // saw the widget), surface a small banner linking to the widget.
      api.get("/account/broker-truth-reconciliation").then(({ data: rd }) => {
        if (rd?.newly_divergent?.length > 0) setReconAlert(rd.newly_divergent);
      }).catch(() => {});
      // Fetch regimes for stock positions (fire and forget)
      const tickers = [...new Set((posRes.data.stocks || []).map(p => p.ticker))];
      tickers.forEach(t => {
        api.get(`/regime/${t}`).then(({ data: rd }) => {
          setPosRegimes(prev => ({ ...prev, [t]: rd }));
        }).catch(() => {});
      });
    } catch (err) { console.error("Dashboard load error:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const savePortfolioSettings = async (updates) => {
    try { await api.put("/portfolio/settings", updates); loadData(); } catch (e) { console.warn(e); }
  };

  // No full-page loading gate — every section mounts immediately and loads in
  // parallel (the old gate serialized panel fetches behind summary/positions/
  // scanner, doubling time-to-interactive and hiding CapacityRiskPanel).
  const s = summary || {};

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Dashboard</h1>
        <div className="flex items-center gap-2">
          <PortfolioSettings s={s} onSave={savePortfolioSettings} />
          <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
            {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
          </span>
        </div>
      </div>

      {/* Iter151 P0: Price-feed degradation banner. Distinct from the
          "House market data — not live" label in Layout — that just tells the user
          feeds are ~15min delayed. THIS banner surfaces when we're serving
          persistently CACHED (potentially quite stale) prices because a
          provider actually failed. Prevents users from reading $-149k EL
          as real data. */}
      {(s.price_feed_degraded || s.price_feed_all_zero) && (
        <div className="border rounded-sm px-3 py-2 flex items-center gap-2 text-[11px]"
          style={{
            background: s.price_feed_all_zero ? "rgba(239,68,68,0.10)" : "rgba(245,158,11,0.10)",
            borderColor: s.price_feed_all_zero ? "rgba(239,68,68,0.4)" : "rgba(245,158,11,0.4)",
            color: s.price_feed_all_zero ? "#F87171" : "#F59E0B",
          }}
          data-testid="price-feed-degraded-banner">
          <span className="font-bold">
            {s.price_feed_all_zero ? "Prices unavailable" : "Prices may be delayed"}
          </span>
          <span style={{ color: "var(--hoip-text-secondary)" }}>
            {s.price_feed_all_zero
              ? "Prices may be delayed if broker connection is not present. Numbers below reflect cash only."
              : "Prices may be delayed if broker connection is not present. NLV / Excess Liquidity / Buying Power may drift as market moves."
            }
          </span>
        </div>
      )}

      {/* Iter156 — subtle broker-truth divergence nudge (small pill,
          not modal). Links to Portfolio → PIE tab where the widget lives. */}
      {reconAlert && reconAlert.length > 0 && (
        <div
          className="border rounded-sm px-3 py-1.5 flex items-center gap-2 text-[11px] cursor-pointer hover:opacity-90"
          style={{
            background: "rgba(239,68,68,0.06)",
            borderColor: "rgba(239,68,68,0.30)",
            color: "#F87171",
          }}
          onClick={() => navigate("/portfolio?tab=pie")}
          data-testid="broker-truth-divergence-nudge"
        >
          <span className="font-bold">⚠ New broker-truth divergence</span>
          <span style={{ color: "var(--hoip-text-secondary)" }}>
            {reconAlert.map(d => d.field.replace(/_/g, " ")).join(", ")} flipped to divergent.
            Open Portfolio → PIE to review.
          </span>
        </div>
      )}

      {/* Row 1 — condensed metrics (half) + Portfolio Risk Gauge (half) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="flex flex-col gap-3">
          {!summary ? (
            <div className="border p-3 flex items-center gap-2 text-[10px]"
              style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
              data-testid="dashboard-metrics-loading">
              <RefreshCw className="w-3 h-3 animate-spin" /> Loading portfolio summary…
            </div>
          ) : (
          <div data-testid="dashboard-metrics">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 content-start">
          <MetricCard label="Net Liquidation" value={`$${(s.nlv||0).toLocaleString()}`} icon={DollarSign} />
          <MetricCard label="Mkt Value" value={`$${(s.market_value||0).toLocaleString()}`} sub={`P&L: ${s.total_stock_pnl>=0?"+":""}$${(s.total_stock_pnl||0).toLocaleString()}`} icon={TrendingUp} color={s.total_stock_pnl>=0?"var(--hoip-profit)":"var(--hoip-loss)"} />
          <MetricCard label="Cash" value={`$${(s.cash||0).toLocaleString()}`} icon={DollarSign} color={(s.cash||0)<0?"var(--hoip-loss)":"var(--hoip-profit)"} />
          <MetricCard label="Excess Liquidity" value={`$${(s.excess_liquidity||0).toLocaleString()}`} icon={Shield} color={(s.excess_liquidity||0)<0?"var(--hoip-loss)":"var(--hoip-profit)"} />
          {metricsOpen && (
            <>
              <MetricCard label="Maint Margin" value={`$${(s.maint_margin||0).toLocaleString()}`} sub={s.margin_regime==="portfolio_margin"?"PM":"Reg-T"} icon={Activity} />
              <MetricCard label="Buying Power" value={`$${(s.buying_power||0).toLocaleString()}`} icon={Zap} />
              <MetricCard label="Net Theta" value={`$${(s.net_theta||0).toFixed(2)}/d`} icon={Clock} color={s.net_theta>=0?"var(--hoip-profit)":"var(--hoip-loss)"} />
              <MetricCard label="Interest" value={`$${(s.monthly_interest||0).toFixed(0)}/mo`} sub={`${s.interest_rate||0}% APR`} icon={DollarSign} color={(s.monthly_interest||0)>0?"var(--hoip-loss)":"var(--hoip-text-tertiary)"} />
            </>
          )}
          </div>
          <button onClick={toggleMetrics}
            className="w-full flex items-center justify-center gap-1 mt-1 py-0.5 text-[9px] border rounded-sm hover:bg-[var(--hoip-hover)] transition-colors"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
            data-testid="metrics-expand-toggle">
            {metricsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {metricsOpen ? "Less" : `Maint Margin $${(s.maint_margin||0).toLocaleString()} · BP $${(s.buying_power||0).toLocaleString()} · Θ $${(s.net_theta||0).toFixed(0)}/d · Int $${(s.monthly_interest||0).toFixed(0)}/mo`}
          </button>
          </div>
          )}
          {/* Item 1 — capacity-constrained heatmap / earnings panel */}
          <CapacityHeatmap s={s} positions={positions} />
        </div>
        <RiskGaugePanel s={s} />
      </div>

      {/* Row 2 — Performance chart (half) + Wheel Income analytics (half) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PerformanceBreakdownChart />
        <WheelAnalyticsPanel />
      </div>

      {/* Row 3 — margin utilization + capital-at-risk, with Floor Protection nested */}
      <CapacityRiskPanel s={s} />

      {/* Row 4 — Position Management + Hedging minis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="mini-panels-row">
        <PositionMgmtMini data={miniPanels} />
        <HedgingMini data={miniPanels} />
      </div>

      {/* Row 5 — Sector Rotation (half) + Rebalancing/MVO mini (half) — directly below PM/Hedging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectorRotationMini />
        <RebalanceMini />
      </div>

      {/* Positions + Scanner */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <div className="flex items-center justify-between p-3 border-b" style={{ borderColor: "var(--hoip-border)" }}>
            <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Open Positions</h2>            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{positions.stocks.length + positions.options.length} total</span>
              <button onClick={() => navigate("/portfolio")} className="text-[10px] underline" style={{ color: "var(--hoip-info)" }} data-testid="view-all-positions">View All</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table" data-testid="dashboard-positions-table">
              <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Type</th>
                <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Regime</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Qty</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }} title="Average cost per share (stocks) or premium per share at entry (options)">Cost/Sh</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }} title="Last cached price (options: the underlying's last price)">Last</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>P&L</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }} title="Stocks: last vs avg cost. Options: intrinsic-value P&L vs premium at entry.">%P&L</th>
              </tr></thead>
              <tbody>
                {positions.stocks.slice(0, 10 - Math.min(positions.options.length, 4)).map((p, i) => {
                  const rg = posRegimes[p.ticker];
                  const stateColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };
                  return (
                  <tr key={`s-${i}`} className="border-b hover:bg-[var(--hoip-hover)]" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="font-mono-data text-[10px] font-medium cursor-pointer text-blue-400 hover:underline" onClick={() => navigate(`/ticker/${p.ticker}`)}>{p.ticker}</td>
                    <td className="text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>Stock</td>
                    <td>{rg ? <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold" style={{ color: stateColors[rg.dominant_state_label], background: `${stateColors[rg.dominant_state_label]}15` }}>{rg.dominant_state_label?.split(" ")[0]}</span> : <span className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}</td>
                    <td className="text-right font-mono-data text-[10px]">{p.shares}</td>
                    <td className="text-right font-mono-data text-[10px]">${p.avg_cost?.toFixed(2)}</td>
                    <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
                      {p.last_price != null ? `$${p.last_price.toFixed(2)}` : "—"}
                    </td>
                    <td className={`text-right font-mono-data text-[10px] ${p.unrealized_pnl == null ? "" : p.unrealized_pnl >= 0 ? "text-emerald-500" : "text-red-400"}`}
                      title={p.last_price ? `Mark $${p.last_price.toFixed(2)} vs cost $${p.avg_cost?.toFixed(2)}` : "No cached mark"}>
                      {p.unrealized_pnl == null ? "—" : `${p.unrealized_pnl >= 0 ? "+" : "-"}$${Math.abs(Math.round(p.unrealized_pnl)).toLocaleString()}`}
                    </td>
                    <td className={`text-right font-mono-data text-[10px] ${(p.last_price && p.avg_cost) ? (p.last_price >= p.avg_cost ? "text-emerald-500" : "text-red-400") : ""}`}
                      data-testid={`stock-pnl-pct-${p.ticker}-${i}`}>
                      {(p.last_price && p.avg_cost) ? `${p.last_price >= p.avg_cost ? "+" : ""}${((p.last_price - p.avg_cost) / p.avg_cost * 100).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                  );
                })}
                {positions.options.slice(0, Math.max(0, 10 - Math.min(positions.stocks.length, 10 - Math.min(positions.options.length, 4)))).map((p, i) => (
                  <tr key={`o-${i}`} className="border-b hover:bg-[var(--hoip-hover)]" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="font-mono-data text-[10px] font-medium">{p.ticker}</td>
                    <td className="text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>{p.strategy_type?.replace(/_/g, " ")}</td>
                    <td><span className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</span></td>
                    <td className="text-right font-mono-data text-[10px]">{p.qty}</td>
                    <td className="text-right font-mono-data text-[10px]">${p.premium?.toFixed(2)}</td>
                    <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}
                      title="Underlying's last cached price (option marks need live chain quotes — see Position Management)">
                      {p.last_price != null ? `$${p.last_price.toFixed(2)}` : "—"}
                    </td>
                    <td className={`text-right font-mono-data text-[10px] ${p.pnl_est == null ? "" : p.pnl_est >= 0 ? "text-emerald-500" : "text-red-400"}`}
                      title={p.pnl_est == null
                        ? "No cached price for the underlying yet — options are marked precisely in Position Management (needs live chain quotes)"
                        : "Intrinsic-value estimate from the underlying's cached price — time value excluded. Precise marks live in Position Management."}
                      data-testid={`option-pnl-${p.ticker}-${i}`}>
                      {p.pnl_est == null ? "—" : (
                        <>
                          {`${p.pnl_est >= 0 ? "+" : "-"}$${Math.abs(Math.round(p.pnl_est)).toLocaleString()}`}
                          {p.pnl_asof && (
                            <span className="ml-1 text-[8px] font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>
                              as of {new Date(p.pnl_asof).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className={`text-right font-mono-data text-[10px] ${(p.pnl_est != null && p.premium) ? (p.pnl_est >= 0 ? "text-emerald-500" : "text-red-400") : ""}`}
                      title="Intrinsic-value P&L as % of premium at entry (× contracts × 100)"
                      data-testid={`option-pnl-pct-${p.ticker}-${i}`}>
                      {(p.pnl_est != null && p.premium) ? `${p.pnl_est >= 0 ? "+" : ""}${(p.pnl_est / (Math.abs(p.premium) * 100 * (p.qty || 1)) * 100).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
                {(positions.stocks.length + positions.options.length) > 10 && (
                  <tr><td colSpan={8} className="text-center py-1.5 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                    Showing 10 of {positions.stocks.length + positions.options.length} — <button onClick={() => navigate("/portfolio")} className="underline" style={{ color: "var(--hoip-info)" }} data-testid="positions-show-all">View All</button>
                  </td></tr>
                )}
                {positions.stocks.length===0&&positions.options.length===0&&(<tr><td colSpan={8} className="text-center py-4 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{loading ? "Loading positions…" : "No open positions."}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <WheelTopCard />
      </div>

      {/* Row 7 — market-wide Risk Intelligence matrix (broader context, after position-level detail) */}
      <MarketRiskPanel macro={miniPanels?.macro} />
    </div>
  );
}
